import { useRequiredContext } from "@redotech/react-util/context";
import AlignLeft01Svg from "@redotech/redo-icons/align-left-01_filled.svg";
import AlignRight01Svg from "@redotech/redo-icons/align-right-01_filled.svg";
import ChevronDownSVG from "@redotech/redo-icons/chevron-down_filled.svg";
import ChevronUpSVG from "@redotech/redo-icons/chevron-up_filled.svg";
import XCloseSVG from "@redotech/redo-icons/x-close.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  ConversationPlatform,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { getCustomerDisplayName } from "@redotech/redo-model/customer";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Button, ButtonSize, ButtonTheme } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import { InputSize } from "@redotech/redo-web/input";
import { Text } from "@redotech/redo-web/text";
import { TextInput } from "@redotech/redo-web/text-input";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { sinkPromise } from "@redotech/util/promise";
import { browserIsMac } from "@redotech/web-util/browser";
import classNames from "classnames";
import {
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { ActiveUsers } from "./active-users";
import { updateConversation } from "./client/conversations";
import * as conversationHeaderCss from "./conversation-header.module.css";
import { ActiveViewContext } from "./conversations-table-filters/active-view-context";
import { ActiveViewConversationCountsContext } from "./conversations-table-filters/conversation-counts-context";
import { UniqueFiltersContext } from "./conversations-table-filters/filters-context";
import { hotkeyTooltip } from "./message-input/hotkeys";
import { TeamConversationActivityContext } from "./support";

export const ConversationHeader = memo(function ConversationHeader({
  conversation,
  leftPanelOpen,
  handleSetLeftPanelOpen,
  rightPanelOpen,
  handleSetRightPanelOpen,
  actionButtons,
  setActiveConversation,
  onConversationsViewed,
  nextConversationInList,
  pendingNextConversation,
  prevConversationInList,
}: {
  conversation: MinimalConversation;
  leftPanelOpen: boolean;
  handleSetLeftPanelOpen: (value: boolean) => void;
  rightPanelOpen: boolean;
  handleSetRightPanelOpen: (value: boolean) => void;
  actionButtons: ReactNode;
  setActiveConversation: (
    conversation: MinimalConversation | undefined,
  ) => void;
  onConversationsViewed: (
    conversation: MinimalConversation,
  ) => MinimalConversation | undefined;
  nextConversationInList?: MinimalConversation;
  pendingNextConversation?: boolean;
  prevConversationInList?: MinimalConversation;
}) {
  const navigate = useNavigate();
  const handleBackClick = () => {
    const searchParams = new URLSearchParams(location.search);
    const ACTIVE_CONVERSATION_QUERY_PARAMETER = "activeConversationId";
    searchParams.delete(ACTIVE_CONVERSATION_QUERY_PARAMETER);
    navigate(`${location.pathname}?${searchParams}`);
    setActiveConversation(undefined);
  };

  useEffect(() => {
    setConversationSubject(conversation.subject);
  }, [conversation]);

  const client = useRequiredContext(RedoMerchantClientContext);
  const canEditSubject = useHasPermission(SupportPermissions.CREATE_REPLY);

  const [conversationSubject, setConversationSubject] = useState(
    conversation.subject,
  );

  async function handleSetSubject() {
    if (!conversationSubject) {
      setConversationSubject(conversation.subject);
    } else {
      await updateConversation(client, conversation, {
        subject: conversationSubject,
      });
    }
  }

  const isMac = browserIsMac();

  const teamConversationActivity = useContext(TeamConversationActivityContext);

  const shouldShowSubheaderBecauseThereAreOtherUsersViewing =
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    teamConversationActivity?.[conversation._id]?.viewing?.length > 1;

  const conversationCounts = useContext(ActiveViewConversationCountsContext);
  const activeView = useContext(ActiveViewContext);
  const filters = useContext(UniqueFiltersContext);

  const filterStatus = filters.status || "all";

  const totalConversations = useMemo(() => {
    return conversationCounts
      ? (conversationCounts[`${activeView.name}-${filterStatus}`] ?? 0)
      : 0;
  }, [conversationCounts, activeView, filterStatus]);

  const shouldShowSubheaderBecauseWeHaveProximateConversations =
    !!nextConversationInList || !!prevConversationInList;

  const shouldDisableButtonsBecauseLoading = pendingNextConversation;

  const shouldDisableNavigateUp =
    !prevConversationInList || shouldDisableButtonsBecauseLoading;

  const shouldDisableNavigateDown =
    !nextConversationInList || shouldDisableButtonsBecauseLoading;

  const shouldShowSubheader =
    shouldShowSubheaderBecauseThereAreOtherUsersViewing ||
    shouldShowSubheaderBecauseWeHaveProximateConversations;

  const handleNavigateUp = () => {
    if (shouldDisableNavigateUp) {
      return;
    }
    onConversationsViewed(prevConversationInList);
    setActiveConversation(prevConversationInList);
  };

  const handleNavigateDown = () => {
    if (shouldDisableNavigateDown) {
      return;
    }
    onConversationsViewed(nextConversationInList);
    setActiveConversation(nextConversationInList);
  };

  useEffect(
    function handleHotkeysForNavigation() {
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.code === "KeyJ" && e.altKey) {
          e.preventDefault();
          e.stopPropagation();
          handleNavigateUp();
        } else if (e.code === "KeyK" && e.altKey) {
          e.preventDefault();
          e.stopPropagation();
          handleNavigateDown();
        }
      };

      addEventListener("keydown", onKeyDown);

      return () => removeEventListener("keydown", onKeyDown);
    },
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      nextConversationInList,
      prevConversationInList,
      shouldDisableNavigateDown,
      shouldDisableNavigateUp,
    ],
  );

  return (
    <Flex
      className={classNames(
        conversationHeaderCss.headerBar,
        ...(!shouldShowSubheader ? [conversationHeaderCss.collapsed] : []),
      )}
      dir="column"
      gap="xs"
      pb="sm"
    >
      <Flex
        align="center"
        gap="3xl"
        justify="space-between"
        pl="2xl"
        pr="2xl"
        pt="md"
      >
        <Flex
          align="center"
          className={conversationHeaderCss.leftHeaderContent}
          gap="sm"
        >
          <Button
            className={conversationHeaderCss.headerButtons}
            onClick={() => handleSetLeftPanelOpen(!leftPanelOpen)}
            size={ButtonSize.NANO}
            theme={ButtonTheme.OUTLINED}
          >
            <Flex
              align="center"
              className={conversationHeaderCss.buttonIconWrapper}
              justify="center"
            >
              {leftPanelOpen ? <AlignLeft01Svg /> : <AlignRight01Svg />}
            </Flex>
          </Button>

          <Button
            className={classNames(
              conversationHeaderCss.headerButtons,
              conversationHeaderCss.justIconButtons,
            )}
            onClick={handleBackClick}
            size={ButtonSize.NANO}
            theme={ButtonTheme.SOLID_LIGHT}
          >
            <Flex
              align="center"
              className={classNames(conversationHeaderCss.buttonIconWrapper)}
              justify="center"
            >
              <XCloseSVG />
            </Flex>
          </Button>
          <Text fontSize="sm" fontWeight="bold" textColor="primary">
            {getCustomerDisplayName(conversation.customer)}
          </Text>

          {conversation.platform === "email" && (
            <>
              <Text
                color="var(--redo-colors-text-text-placeholder_subtle)"
                fontSize="sm"
                fontWeight="regular"
              >
                |
              </Text>
              <Flex className={conversationHeaderCss.subjectContainer}>
                <TextInput
                  color="tertiary"
                  ellipsis
                  fullwidth
                  hideFocus
                  onChange={setConversationSubject}
                  onFocus={(focused) => {
                    if (!focused) {
                      sinkPromise(handleSetSubject());
                    }
                  }}
                  readonly={!canEditSubject}
                  showBorder={false}
                  size={InputSize.SMALL}
                  value={conversationSubject}
                />
              </Flex>
            </>
          )}
          {(conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS ||
            conversation.platform === ConversationPlatform.FACEBOOK ||
            conversation.platform === ConversationPlatform.INSTAGRAM ||
            conversation.platform ===
              ConversationPlatform.INSTAGRAM_COMMENTS) &&
            conversation?.metaPageName && (
              <Text fontSize="sm" fontWeight="regular">
                | {conversation?.metaPageName}
              </Text>
            )}
        </Flex>
        <Flex align="center" gap="md" justify="flex-end">
          <Text fontSize="sm" textColor="secondary">
            Ticket #{conversation.ticketNumber}
          </Text>
          {actionButtons}

          <Button
            className={conversationHeaderCss.headerButtons}
            onClick={() => {
              handleSetRightPanelOpen(!rightPanelOpen);
            }}
            size={ButtonSize.NANO}
            theme={ButtonTheme.OUTLINED}
          >
            <Flex
              align="center"
              className={conversationHeaderCss.buttonIconWrapper}
              justify="center"
            >
              {rightPanelOpen ? <AlignRight01Svg /> : <AlignLeft01Svg />}
            </Flex>
          </Button>
        </Flex>
      </Flex>
      {shouldShowSubheader && (
        <Flex grow={1} justify="space-between" pl="2xl" pr="2xl">
          <Flex>
            {shouldShowSubheaderBecauseThereAreOtherUsersViewing && (
              <ActiveUsers conversation={conversation} maxAvatars={4} />
            )}
          </Flex>

          <Flex align="center" gap="md" justify="flex-end">
            {shouldShowSubheaderBecauseWeHaveProximateConversations && (
              <>
                {totalConversations > 1 && (
                  <Flex align="center" gap="none">
                    <Text fontSize="xs" fontWeight="bold">
                      {`${totalConversations}`}
                    </Text>
                    <Text fontSize="xs">
                      {` conversation${totalConversations === 1 ? "" : "s"}`}
                    </Text>
                  </Flex>
                )}

                <Tooltip
                  title={hotkeyTooltip({
                    action: "Navigate",
                    code: "J",
                    name: "up",
                    isMac,
                    modifierKey: "alt",
                  })}
                >
                  <span>
                    <RedoButton
                      disabled={shouldDisableNavigateUp}
                      hierarchy="secondary"
                      IconLeading={() => <ChevronUpSVG />}
                      onClick={handleNavigateUp}
                    />
                  </span>
                </Tooltip>
                <Tooltip
                  title={hotkeyTooltip({
                    action: "Navigate",
                    code: "K",
                    name: "down",
                    isMac,
                    modifierKey: "alt",
                  })}
                >
                  <span>
                    <RedoButton
                      disabled={shouldDisableNavigateDown}
                      hierarchy="secondary"
                      IconLeading={() => <ChevronDownSVG />}
                      onClick={handleNavigateDown}
                    />
                  </span>
                </Tooltip>
              </>
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});
